import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, CheckCircle2, ChevronRight, TriangleAlert } from 'lucide-react';
import type { AttentionItem } from '@/lib/attention';
import { cn } from '@/lib/utils';

export interface AttentionStripProps {
  /** undefined while still loading. */
  items: AttentionItem[] | undefined;
  /** True when a critical data source failed, so the list may be missing items. */
  incomplete?: boolean;
}

const COLLAPSED_COUNT = 5;

/** Top-of-dashboard summary of everything that needs operator attention. */
export function AttentionStrip({ items, incomplete = false }: AttentionStripProps) {
  const [expanded, setExpanded] = useState(false);
  const headingId = useId();
  const listId = useId();

  if (items === undefined) return null;

  if (items.length === 0 && incomplete) {
    return (
      <div role="status" className="flex items-center gap-2 rounded-lg border bg-status-warning-soft px-4 py-3 text-sm text-status-warning">
        <TriangleAlert className="size-4 shrink-0" aria-hidden />
        <span>Couldn&apos;t verify system health — some data failed to load.</span>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div role="status" className="flex items-center gap-2 rounded-lg border bg-card px-4 py-3 text-sm text-muted-foreground">
        <CheckCircle2 className="size-4 shrink-0" aria-hidden />
        <span>All systems healthy</span>
      </div>
    );
  }

  const hasDanger = items.some((i) => i.severity === 'danger');
  const hiddenCount = Math.max(0, items.length - COLLAPSED_COUNT);
  const isExpanded = expanded && hiddenCount > 0;
  const visible = isExpanded ? items : items.slice(0, COLLAPSED_COUNT);

  return (
    <section
      aria-labelledby={headingId}
      className={cn('space-y-2 rounded-lg border px-4 py-3', hasDanger ? 'bg-status-danger-soft' : 'bg-status-warning-soft')}
    >
      <h2 id={headingId} className="flex items-center gap-2 text-sm font-semibold">
        <AlertCircle className="size-4 shrink-0" aria-hidden />
        Needs attention ({items.length})
      </h2>
      {incomplete && (
        <p className="text-xs text-muted-foreground">Some checks couldn&apos;t run — this list may be incomplete.</p>
      )}
      <ul id={listId} className="space-y-1">
        {visible.map((item) => {
          const Icon = item.severity === 'danger' ? AlertCircle : TriangleAlert;
          return (
            <li key={item.id}>
              <Link
                to={item.to}
                className="flex min-w-0 items-start gap-2 rounded-sm py-1 text-sm outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Icon
                  className={cn('mt-0.5 size-4 shrink-0', item.severity === 'danger' ? 'text-status-danger' : 'text-status-warning')}
                  aria-hidden
                />
                <span className="min-w-0 break-words">
                  <span className="sr-only">{item.severity === 'danger' ? 'Critical:' : 'Warning:'} </span>
                  <span className="font-medium">{item.title}</span>
                  {item.detail && <span className="ml-2 text-muted-foreground">{item.detail}</span>}
                </span>
                <ChevronRight className="ml-auto mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
              </Link>
            </li>
          );
        })}
      </ul>
      {hiddenCount > 0 && (
        <button
          type="button"
          aria-expanded={isExpanded}
          aria-controls={listId}
          onClick={() => setExpanded((v) => !v)}
          className="rounded-sm text-sm font-medium underline-offset-2 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
        >
          {isExpanded ? 'Show fewer' : `Show ${hiddenCount} more`}
        </button>
      )}
    </section>
  );
}
